import { useMemo } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
} from '@tanstack/react-table';

import { cn } from '@simplywallst/ui-core/next';

import { getTradingItemCurrencyISO } from '@/utilities/getTradingItemCurrencyISO';

import type { PortfolioDetailedReturns } from '../../hooks/usePortfolioDetailedReturns';

import { TABLE_OVERVIEW_COLUMN_LENGTH } from '../TableOverview/utils';
import { getTransactionsColumns } from './utils';
import { getDateRangeString } from '../../utils';

import { HoldingOverview } from '../HoldingOverview';
import type { PortfolioType } from '../../../HoldingsTable/hooks';
type Holding = PortfolioDetailedReturns['holdings'][number];

type Props = {
  currencyISO: string;
  holding: Holding;
  portfolioType: PortfolioType;
};

export const PopulatedState = ({
  currencyISO,
  holding,
  portfolioType,
}: Props) => {
  const company = holding.company;
  const holdingCurrencyISO =
    getTradingItemCurrencyISO(company.data.currencyInfo) ?? 'XXX';

  const columns = useMemo(
    () =>
      getTransactionsColumns({
        portfolioCurrencyISO: currencyISO,
        holdingCurrencyISO,
      }),
    [currencyISO, holdingCurrencyISO]
  );

  const transactions = useReactTable({
    getCoreRowModel: getCoreRowModel(),
    columns,
    data: holding.detailedTransactions,
  });

  const rows = useMemo(() => transactions.getRowModel().rows, [transactions]);

  const cell = cn(
    'border-b border-l-0 border-r border-t-0 border-solid border-soft px-x2 py-x1_5'
  );

  const bodyCell = cn(cell, 'text-xs text-solid');

  const headerCell = cn(
    cell,
    'bg-surface-2 align-bottom font-strong leading-tight'
  );

  return (
    <>
      <HoldingOverview
        currencyISO={currencyISO}
        holding={holding}
        portfolioType={portfolioType}
      />
      <tbody>
        <tr aria-label={`${holding.company.uniqueSymbol} Transactions`}>
          <th
            colSpan={TABLE_OVERVIEW_COLUMN_LENGTH}
            className="border-x-0 border-b border-t-0 border-solid border-soft px-x2 py-x0_75 text-left text-tiny font-strong text-soft"
          >
            <span className="uppercase">
              Transactions {` (${holding.detailedTransactions.length}) `}
            </span>
            {` `}
            <span className="ml-x0_75 text-softer">
              {getDateRangeString(
                holding.detailedReturns.startDate,
                holding.detailedReturns.endDate
              )}
            </span>
          </th>
        </tr>

        {transactions.getHeaderGroups().map((headerGroup) => (
          <tr
            key={headerGroup.id}
            className="text-left text-tiny text-soft [&_th:nth-child(n+4)]:text-right"
          >
            {headerGroup.headers.map((header) => (
              <th
                key={header.id}
                colSpan={
                  header.index === headerGroup.headers.length - 1 ? 3 : 2
                }
                className={headerCell}
              >
                {flexRender(
                  header.column.columnDef.header,
                  header.getContext()
                )}
              </th>
            ))}
          </tr>
        ))}

        {rows.map((row) => {
          const cells = row.getVisibleCells();
          return (
            <tr
              key={row.id}
              className="text-left text-tiny text-soft [&_td:nth-child(n+4)]:text-right"
            >
              {cells.map((cell, index) => (
                <td
                  key={cell.id}
                  colSpan={index === cells.length - 1 ? 3 : 2}
                  className={bodyCell}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          );
        })}
      </tbody>
    </>
  );
};
